import type { RoomsParticipantDistribution as RoomsParticipantDistributionType } from '@/domain/shared/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Popover } from '@sunwebgroup/uds-ui/popover'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { use, useEffect, useState } from 'react'
import { useIntl } from '@/intl'
import * as ParticipantsFilterPrimitive from '@/scopes/common/components/Filters/Participants'
import { useParticipantsFilter } from '@/scopes/common/components/Filters/Participants/hooks'
import {
  ChildInfo,
  ChildWithoutAdultRoomInfo,
  GroupLink,
} from '@/scopes/common/components/Filters/Participants/ParticipantsBanner'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  getPricesSelector,
  participantsSelector,
} from '@/scopes/prices/components/selectors'
import { createPricesFilterClickEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'
import { BaseInput } from '@/scopes/search/search/pages/SearchPage/components/Filters/BaseInput'
import { FormField, FormFieldControl, FormFieldLabel } from '@/scopes/search/search/pages/SearchPage/components/Filters/FormField'
import styles from './ParticipantsDropdownFilter.module.css'

interface ParticipantsDropdownFilterProps {
  isFlightOnly: boolean
}

export function ParticipantsDropdownFilter({ isFlightOnly = false }: ParticipantsDropdownFilterProps) {
  const intl = useIntl()
  const { selectFilter } = use(PricesActionsContext)
  const [isOpen, setIsOpen] = useState(false)
  const { selectedValues: selectedParticipants } = usePricesSelector(participantsSelector)
  const { sendEvent } = useAnalytics()
  const { loading: isFetching } = usePricesSelector(getPricesSelector)
  const {
    handleConfirm,
    getFormattedText,
    showParticipants,
    showChildren,
    roomsParticipantDistribution,
    setRooms,
    warningNoBabiesWithoutAdults,
  } = useParticipantsFilter({
    isFetching,
    setSelection: setParticipantsSelection,
    isFlightOnly,
  })
  const disabledSaveAction = warningNoBabiesWithoutAdults

  useEffect(() => {
    if (!selectedParticipants)
      return
    setRooms(selectedParticipants)
  }, [selectedParticipants, setRooms])

  function setParticipantsSelection(selection: RoomsParticipantDistributionType) {
    selectFilter({ type: 'Participants', selection })
  }

  function toggle() {
    if (!isFetching) {
      if (!disabledSaveAction) {
        handleConfirm()
      }

      if (isOpen) {
        sendEvent(
          createPricesFilterClickEvent({
            element: 'participants selector',
            type: 'partyComposition',
            result: formatParticipantDistributionToGA4Result(roomsParticipantDistribution).distribution,
          }),
        )
      }
      setIsOpen(!isOpen)
    }
  }

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'prices_filters_participants_label' })}</FormFieldLabel>
      <FormFieldControl>
        <Popover.Root open={isOpen} onOpenChange={toggle}>
          <Popover.Trigger data-qa="participants-filter-input">
            <BaseInput.Root>
              <BaseInput.Box tabIndex={isFetching ? undefined : 0}>
                <Typography variant="body-m-regular-tight">{getFormattedText().desktop}</Typography>
              </BaseInput.Box>
              <BaseInput.ChevronDown />
            </BaseInput.Root>
          </Popover.Trigger>
          <Popover.Content title={intl.formatMessage({ id: 'prices_filters_participants_label' })} closeText={intl.formatMessage({ id: 'close' })} hasPadding={false}>
            <>
              {(showChildren || showParticipants || warningNoBabiesWithoutAdults) && (
                <ParticipantsFilterPrimitive.DialogBannerWrapper className={styles.header}>
                  {showChildren && !warningNoBabiesWithoutAdults && <ChildInfo />}
                  {warningNoBabiesWithoutAdults && <ChildWithoutAdultRoomInfo />}
                  {showParticipants && <GroupLink />}
                </ParticipantsFilterPrimitive.DialogBannerWrapper>
              )}
              <ParticipantsFilterPrimitive.Body isMultiRoom={!isFlightOnly} className={styles.body} />
              <ParticipantsFilterPrimitive.Footer>
                <Button
                  fullWidth
                  onClick={toggle}
                  disabled={disabledSaveAction}
                  data-qa="participants-close-button"
                >
                  {intl.formatMessage({
                    id: 'quicksearch_save_button_text',
                  })}
                </Button>
              </ParticipantsFilterPrimitive.Footer>
            </>
          </Popover.Content>
        </Popover.Root>
      </FormFieldControl>
    </FormField>
  )
}
